'use client';

import React, { Component } from 'react';
import { withAppRouter } from '@/legacy/withAppRouter';

import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';

import { API_KEY_LIMIT } from '@/constants/api';
import { FV_KEY_NAME } from '@/constants/filters';
import { GALLERY_CONFIG_PRODUCT_SEARCH_GENERAL } from '@/constants/gallery';
import { ICON_SEARCH } from '@/constants/icons';
import {
	TX_INV_ADD_PRODUCT_NO_RESULTS_MATCHING,
	TX_NO_RESULTS,
	TX_PLACEHOLDER_SEARCH,
	TX_SEARCH,
} from '@/constants/strings';
import {
	URL_BUY_GENERAL, 
	URL_BUY_PRODUCT_LINE, 
	URL_SELL_GENERAL,
	URL_SELL_PRODUCT_LINE,
	URL_NS_SELL,
} from '@/constants/urls';

import { getFilterQueryString } from '@/utils/filters';
import { getGalleryFilterStorageKey } from '@/utils/gallery';
import { isCurrentRoute } from '@/utils/general';

import Icon from '@/components/Icons/Icon';

import '../_styles/_headersearch.scss';

import * as productActionCreators from '@/actions/product';
let allActionCreators = Object.assign({}, productActionCreators);

export class HeaderSearch extends Component {

	constructor(props) {
		super(props);

		this.MIN_INPUT_LENGTH = 3;

		this.OPEN_DELAY = 200;

		this.state = {
			autocompleteActive: false,
			autocompleteClosable: false,

			navIndex: -1,

			options: null,

			lastQuery: null,
		};

		this.closeTimeout = null;

		this.autocompleteWrapperRef = React.createRef();
		this.searchInputRef = React.createRef();

		this.controllerAutocomplete = null;

		this.checkClick = this.checkClick.bind(this);
		this.checkKeypress = this.checkKeypress.bind(this);
		this.handleInputKeyDown = this.handleInputKeyDown.bind(this);
		this.submitSearch = this.submitSearch.bind(this);
	}

	componentDidMount() {
		document.addEventListener('click', this.checkClick, false);
		window.addEventListener('keydown', this.checkKeypress, false);
	}

	componentWillUnmount() {
		document.removeEventListener('click', this.checkClick, false);
		window.removeEventListener('keydown', this.checkKeypress, false);

		if(this.closeTimeout) {
			clearTimeout(this.closeTimeout);
		}
		if(this.controllerAutocomplete) {
			this.controllerAutocomplete.abort();
		}
  	}

	checkClick(evt) {
		if(this.state.autocompleteActive && this.state.autocompleteClosable) {
			let targetElement = evt.target;
			do {
				if(this.autocompleteWrapperRef && targetElement === this.autocompleteWrapperRef.current) {
					return null;
				}
				targetElement = targetElement.parentNode;
			} while (targetElement);
			this.closeAutocomplete();
		}
	}

	// Window-level handler, intentionally Escape-only. Enter and the arrow keys now live on the
	// input itself (handleInputKeyDown) and on the form's onSubmit, because (a) HeaderSearch is
	// mounted 2-3 times at once and every instance had its own window listener reading the same
	// redux value, and (b) a window keydown is never delivered at all when Android's IME resolves
	// the action key to IME_ACTION_NEXT, which advances focus instead of dispatching Enter.
	// Escape stays global so the dropdown can still be dismissed when focus has left the input.
	checkKeypress(evt) {
		if(evt && evt.key === 'Escape') {
			this.closeAutocomplete();
		}
	}

	handleInputKeyDown(evt) {

		if(!evt) { return; }

		// Never act on a key that is committing an IME composition (zh/ja input, Android autocorrect
		// suggestions). Those arrive with isComposing true and must fall through to the IME untouched.
		if(evt.nativeEvent && evt.nativeEvent.isComposing) { return; }

		switch(evt.key) {
			case 'ArrowUp':
				evt.preventDefault();
				this.setState({ navIndex: Math.max(this.state.navIndex - 1, 0) });
				break;
			case 'ArrowDown':
				evt.preventDefault();
				this.setState({ navIndex: Math.min(this.state.navIndex + 1, this.state.options ? this.state.options.length - 1 : 0) });
				break;
			case 'Enter':
				// preventDefault here also suppresses the implicit form submission this keydown would
				// otherwise trigger, so exactly one search runs per Enter.
				evt.preventDefault();
				this.handleSelect(this.state.navIndex);
				break;
			default:
				break;
		}
	}

	submitSearch(evt) {

		// preventDefault is mandatory: without it the form performs a real GET navigation and
		// hard-reloads the SPA. This fires for the SEARCH button and for any platform path that
		// submits without first dispatching a keydown we handled (Android IME_ACTION_SEARCH on a
		// keyboard that does not synthesise Enter, iOS 'Go', implicit submission).
		if(evt) { evt.preventDefault(); }

		if(this.state.navIndex > -1) {
			this.handleSelect(this.state.navIndex);
		} else {
			this.searchAction();
		}
	}

	handleSelect(idx) {

		let searchValue = this.props.product.nameSearchValue;
		if(idx > -1 && this.state.options) {
			searchValue = this.state.options.length > idx ? this.state.options[idx].name : '';
		}

		if(searchValue) {
			this.props.productSetNameValue(searchValue);
			this.searchAction(searchValue);
		}
	}

	mouseEnterAction(idx) {
		if(idx !== this.state.navIndex) {
			this.setState({ navIndex: idx });
		}
	}
  
	mouseLeaveAction(idx) {
		if(idx === this.state.navIndex) {
			this.setState({ navIndex: -1 });
		}
	}

	openAutocomplete() {
		this.setState({
			autocompleteActive: true,
			autocompleteClosable: false,
		}, () => {
			this.closeTimeout = setTimeout(() => {
				this.setState({ autocompleteClosable: true });
			}, this.OPEN_DELAY);
		});
	}

	closeAutocomplete(cb = null) {
		this.setState({ 
			autocompleteActive: false,
			autocompleteClosable: false,
			navIndex: -1,
		}, () => {
			if(cb) { cb(); }
		});
	}

	async fetchAutocomplete(searchString = '') {

		// Async call to autocomplete products
		if(this.controllerAutocomplete) {
			this.controllerAutocomplete.abort();
		}
		const controllerAutocomplete = new AbortController();
		this.controllerAutocomplete = controllerAutocomplete;

		const searchVal = searchString.trim() || this.props.product.nameSearchValue.trim();
		const searchParams = {
			[API_KEY_LIMIT]: 10,
		}
		
		const searchResp = await this.props.productAutocomplete(searchVal, searchParams, controllerAutocomplete.signal)
			.catch((errResp) => {
				// Do nothing
			});

		if(!searchResp) { return null; }

		const optionsValue = searchResp.unique && searchResp.unique.length ? searchResp.unique : null;

		this.setState({ 
			options: optionsValue, 
		});

		if(optionsValue !== null && !this.state.autocompleteActive) {
			this.openAutocomplete();
		}
	}

	searchAction(searchString = '') {

		const searchValue = searchString || this.props.product.nameSearchValue || '';
		const passedFilters = {
			[FV_KEY_NAME]: searchValue,
		}

		const isBuylist = window.location.pathname.startsWith(`/${URL_NS_SELL}`);

		const generalUrl = isBuylist ? URL_SELL_GENERAL : URL_BUY_GENERAL;
		const productLineUrl = isBuylist ? URL_SELL_PRODUCT_LINE : URL_BUY_PRODUCT_LINE;

		const skipNav = isCurrentRoute(generalUrl) || isCurrentRoute(productLineUrl);
		this.closeAutocomplete(() => {
			if(skipNav) {

				// this.props.router.replace(window.location.pathname, {filters: passedFilters});
				const filterKey = getGalleryFilterStorageKey(GALLERY_CONFIG_PRODUCT_SEARCH_GENERAL);
				const existingFilters = this.props.product.filters[filterKey] || {};
				this.props.productSetProductFilters({
					key: filterKey,
					value: { ...existingFilters, ...passedFilters },
				});
			} else {
				const filterQueryString = getFilterQueryString(passedFilters);
				this.props.router.push(`${generalUrl}${filterQueryString}`);
			}
		});
	}

	changeName(evt) {

		const inputValue = evt.target.value;

		this.props.productSetNameValue(inputValue);

		if(inputValue.trim().length >= this.MIN_INPUT_LENGTH) {
			this.fetchAutocomplete(inputValue);
		} else {
			this.setState({ options: null });
		}

		if(this.state.options !== null && !this.state.autocompleteActive) {
			this.openAutocomplete();
		}
	}

	render() {

		const {t} = this.props;

		return <div className='HeaderSearch' ref={this.autocompleteWrapperRef}>
		
			<div className='searchInnerWrapper'>
				{/* This form must never be rendered inside another <form>: the HTML parser silently drops
				    nested forms and onSubmit would stop firing at that mount point only. HeaderSearch is
				    mounted from HeaderBuySellPlay, HeaderElementSearchDrawer and ProductSearch; none of
				    those subtrees contain a form today, and the test asserts the form is present. */}
				<form className='searchForm' role='search' onSubmit={this.submitSearch}>
					<div className='searchInputWrapper'>
						<input 
							ref={this.searchInputRef}
							type='search'
							name='q'
							inputMode='search'
							enterKeyHint='search'
							autoComplete='off'
							aria-label={t(TX_PLACEHOLDER_SEARCH)}
							value={this.props.product.nameSearchValue}
							placeholder={t(TX_PLACEHOLDER_SEARCH)}
							onKeyDown={this.handleInputKeyDown}
							onChange={this.changeName.bind(this)} />
					</div>
					<button type='submit' className='searchButtonWrapper' aria-label={t(TX_SEARCH)}>
						<div className='searchButtonInnerWrapper'>{t(TX_SEARCH)}</div>
						<div className='searchButtonInnerWrapperTablet'>
							<Icon 
								value={ICON_SEARCH} 
								iconWrapperClass='searchTabletIcon' />
						</div>
					</button>
				</form>
				<div className={`searchAutocompleteWrapper ${this.state.autocompleteActive ? 'open' : ''}`}>
					{this.state.options !== null && (
						<div className='optionsLiner'>
							{this.state.options.length ?
								<div className='optionsList'>
									{this.state.options.map((product, i) => {
										return <div 
													key={i} 
													className={`optionElementWrapper ${this.state.navIndex === i ? 'hovered' : ''}`}
													onMouseEnter={() => this.mouseEnterAction(i)}
													onMouseLeave={() => this.mouseLeaveAction(i)}
													onClick={() => this.handleSelect(i)}>{product.name}</div>
									})}
								</div> :
								<div className='optionsNull'>
									<div className='nullWrapper'>
										<div className={'noResults'}>
											<div className='nullLiner'>
												{this.state.lastQuery ?
													<>
														<div className='noResultsCopy'>{t(TX_INV_ADD_PRODUCT_NO_RESULTS_MATCHING)}</div>
														<div className='noResultsNeedle'>{this.props.lastQuery}</div>
													</> :
													<div className='noResultsCopy'>{t(TX_NO_RESULTS)}</div>
												}
											</div>
										</div>
									</div>
								</div>
							}
						</div>
					)}
				</div>
			</div>
		</div>;
	}
}

function mapStateToProps(state) {
	return {
		product: state.product
	};
}

export default connect(mapStateToProps, allActionCreators)(withTranslation()(withAppRouter(HeaderSearch)));


